import { push } from "connected-react-router";
import { App, useGetAppListItemQuery, useGetAppsForOrgQuery, useGetTotalAppsForOrgQuery } from "generated/graphql";
import React, { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { appIdFromRoute } from "routes/Organization/orgPageRoutes";
import { isNotNullOrUndefined } from "utils/isNotNullOrUndefined";
import { AppSwitcher } from "./AppSwitcher";

export interface AppSwitcherContainerProps { }

/**
 * Container for the app switcher component in the site header
 */
export const AppSwitcherContainer: FC<AppSwitcherContainerProps> = (props) => {

    const match = useRouteMatch<{ appId?: string, organizationId?: string }>("/:organizationId/:appId");

    const organizationId: string = match?.params.organizationId;
    // Org pages (/{orgId}/leads) and non-org pages (/admin/orgs) are not apps; querying them as
    // one asked the API for app(appId: "leads") on every such page.
    const appId: string = appIdFromRoute(match?.params);

    const [showItems, setShowItems] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");

    let name: string;

    if (search) {
        name = search.trim().toLowerCase();
    }

    const [getApps] = useGetAppsForOrgQuery({
        pause: !showItems,
        requestPolicy: "cache-and-network",
        variables: {
            organizationId,
            from: 0,
            size: 8,
            name,
        },
    });

    const [getApp] = useGetAppListItemQuery({
        pause: !appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId,
        },
    });

    const [getTotalApps] = useGetTotalAppsForOrgQuery({
        requestPolicy: "cache-and-network",
        pause: !organizationId || organizationId === "admin",
        variables: {
            organizationId
        }
    });

    const finished = !getApps.stale && getApps?.data?.org?.apps?.total

    const apps = getApps.data?.org?.apps?.apps.filter(isNotNullOrUndefined) || [];

    // get selected app from the state
    const app = getApp.data?.app;

    // this is used to know if we need to display the drop down button or not
    // if they only have one then they can't change apps!
    const totalApps = getTotalApps.data?.org?.apps?.total;

    const fetching = getApp.fetching;
    const fetchingApps = getApps.fetching;

    const dispatch = useDispatch();

    function onSelectApplication(app: App) {
        dispatch(push(`/${organizationId}/${app.appId}`));
    }

    // Only on a business's own pages. Org pages (members, leads, settings, ...) are not about one
    // business, and the paused app query would still hold the last business visited.
    return organizationId && appId ? (
        <AppSwitcher
            app={app}
            apps={apps}
            totalApps={totalApps}
            search={search}
            isLoading={fetching}
            isLoadingItems={fetchingApps}
            onSelectApplication={onSelectApplication}
            onSearchChange={setSearch}
            showItems={showItems}
            onShowItemsChange={setShowItems}
            finished={finished}
        />
    ) : (
        <></>
    );
};
